/**
 * Redeem a password-reset link (CLAUDE.md 11.6, 11.7).
 *
 * The token arrives in the query string, is used once, and is never stored on the
 * client -- there is nowhere useful to keep it and every extra copy is another place
 * it can leak.
 *
 * Redeeming revokes every session server-side. If this reset is an account recovery
 * after a compromise, leaving the attacker's refresh token alive would defeat the
 * point, so the user is sent back to sign in afterwards rather than being logged
 * straight in.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError, api } from '@/api/client'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'

const MIN_PASSWORD_LENGTH = 12

export function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && confirm !== password

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (tooShort || mismatch) return
    setPending(true)
    setError(null)
    try {
      await api.publicPost('/auth/password/reset', { token, new_password: password })
      setDone(true)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.localised : String(caught))
    } finally {
      setPending(false)
    }
  }

  if (!token) {
    return (
      <AuthLayout title={t('password.reset_title')}>
        <p
          role="alert"
          className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
        >
          {t('errors:error.auth.reset_token_invalid')}
        </p>
        <Button asChild variant="secondary" className="mt-4 w-full">
          <Link to="/forgot-password">{t('password.forgot_title')}</Link>
        </Button>
      </AuthLayout>
    )
  }

  if (done) {
    return (
      <AuthLayout title={t('password.reset_title')}>
        <div className="space-y-4 text-center">
          <CheckCircle2 size={32} className="mx-auto text-[var(--status-pass)]" aria-hidden />
          <p role="status" className="text-sm">
            {t('password.reset_done')}
          </p>
          <Button variant="primary" className="w-full" onClick={() => navigate('/login')}>
            {t('login.submit')}
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={t('password.reset_title')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && (
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <Field
          description={t('register.password_hint')}
          error={
            tooShort ? t('error.auth.password_too_short', { minimum: MIN_PASSWORD_LENGTH }) : null
          }
        >
          <FieldLabel>{t('password.new')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field error={mismatch ? t('password.mismatch') : null}>
          <FieldLabel>{t('password.confirm')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={pending}
          disabled={tooShort || mismatch || !password}
        >
          {t('password.reset_submit')}
        </Button>
      </form>
    </AuthLayout>
  )
}
